import React, { useState, useEffect } from 'react';
import PageWrapper from '../components/PageWrapper';
import PageTitle from '../components/PageTitle';
import Card from '../components/Card';
import BuildingInfo from '../components/BuildingInfo';
import BuildingImage from '../assets/images/building.png';
import UserContacts from '../components/UserContacts';
import BlockTitle from '../components/BlockTitle';
import DetailsList from '../components/DetailsList';
import CustomerEmailHistory from '../components/CustomerEmailHistory';
import CardSkeleton from '../components/CardSkeleton';
import TableSkeleton from '../components/TableSkeleton';
import EditClientModal from '../components/EditClientModal';
import Button from '../components/Button';
import ConfirmDialog from '../components/ConfirmDialog';
import { collection, doc, getDoc, getDocs, writeBatch } from "firebase/firestore";
import { firestore } from '../lib/firebaseConfig';
import { useLocation, useNavigate } from 'react-router-dom';
import EditableInstallmentsTable from '../components/EditableInstallmentsTable';
import EditFlatRemindersModal from '../components/EditFlatRemindersModal';
import FinalInvoicesSection from '../components/FinalInvoicesSection';
import SurchargeSection from '../components/SurchargeSection';
import GenerateInvoiceModal from '../components/GenerateInvoiceModal';
import GenerateZaumdeModal from '../components/GenerateZaumdeModal';
import { useToast } from '../context/ToastContext';
import { useInvestment } from '../context/InvestmentContext';
import { grossToNet } from '../lib/vat';

const Payments = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const { selectedInvestmentId } = useInvestment();
  const { flatId, clientId, investmentId: routeInvestmentId } = location.state || {};
  const investmentId = routeInvestmentId || selectedInvestmentId;
  const [flatDetails, setFlatDetails] = useState(null);
  const [clientsDetails, setClientsDetails] = useState([]);
  const [investmentDetails, setInvestmentDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  // bump po zapisie danych DFG, zeby ponowne otwarcie formularza mialo swiezy prefill
  const [refreshKey, setRefreshKey] = useState(0);


  useEffect(() => {
const fetchFlatDetails = async () => {
  if (flatId && investmentId) {
    try {
      // Fetch flat document
      const flatDocRef = doc(firestore, `investments/${investmentId}/flats/${flatId}`);
      const flatDoc = await getDoc(flatDocRef);

      if (flatDoc.exists()) {
        const flatData = flatDoc.data();

        // Fetch payments collection
        const paymentsCollectionRef = collection(firestore, `investments/${investmentId}/flats/${flatId}/payments`);
        const paymentsSnapshot = await getDocs(paymentsCollectionRef);

        // Count payments
        const paymentsCount = paymentsSnapshot.size;

        // Optional: Set the count in state or combine with flat details
        setFlatDetails({
          ...flatData,
          paymentsCount: paymentsCount
        });

        console.log(`✅ Fetched flat details and found ${paymentsCount} payments.`);
      } else {
        console.error('Flat not found.');
      }
    } catch (error) {
      console.error('Error fetching flat details:', error);
    }
  }
};

    const fetchClientsDetails = async () => {
      if (clientId && clientId.length > 0) {
        try {
          const clientDocs = await Promise.all(
            clientId.map((id) => getDoc(doc(firestore, `customers/${id}`)))
          );
          const clients = clientDocs
            .filter((doc) => doc.exists())
            .map((doc) => ({ id: doc.id, ...doc.data() }));
          setClientsDetails(clients);
        } catch (error) {
          console.error('Error fetching clients:', error);
        }
      }
    };

    const fetchInvestmentDetails = async () => {
      if (investmentId) {
        try {
          const investmentDocRef = doc(firestore, `investments/${investmentId}`);
          const investmentDoc = await getDoc(investmentDocRef);
          if (investmentDoc.exists()) {
            setInvestmentDetails(investmentDoc.data());
          }
        } catch (error) {
          console.error('Error fetching investment:', error);
        }
      }
    };

    const fetchData = async () => {
      setLoading(true);
      try {
        await Promise.all([fetchFlatDetails(), fetchClientsDetails(), fetchInvestmentDetails()]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [flatId, clientId, investmentId, refreshKey]);

  // Dynamic Customer Details
  const CUSTOMER_DETAILS = flatDetails
    ? [
        { title: 'Umowa deweloperska', value: flatDetails.contract_number || '-' },
        { title: 'Data', value: flatDetails.contract_date || '-' },
        { title: 'Mieszkanie', value: flatDetails.code || '-' },
        { title: 'Powierzchnia', value: `${flatDetails.size || '-'} m²` },
      ]
    : [];


  // Dynamic Other Details
  const CUSTOMER_OTHER_DETAILS = flatDetails
    ? [
        { title: 'Cena netto', value: `${flatDetails.an_amount ? Math.round(grossToNet(flatDetails.an_amount, flatDetails)) : '-'} zł` },
        { title: 'Cena brutto', value: `${flatDetails.an_amount || '-'} zł` },
        { title: 'Liczba rat', value: flatDetails.paymentsCount || '-' },
      ]
    : [];
const [isEditInvestmentModalOpen, setIsEditInvestmentModalOpen] = useState(false);
const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
const [isZaumdeModalOpen, setIsZaumdeModalOpen] = useState(false);

const closeEditInvestmentModal = () => {
  setIsEditInvestmentModalOpen(false);
};

// Client edit handlers
const handleEditClient = (client) => {
  setSelectedClient(client);
  setIsEditModalOpen(true);
};

const handleCloseEditModal = () => {
  setIsEditModalOpen(false);
  setSelectedClient(null);
};

const handleClientSaved = async () => {
  // Refresh clients data
  if (clientId && clientId.length > 0) {
    const clientDocs = await Promise.all(
      clientId.map((id) => getDoc(doc(firestore, `customers/${id}`)))
    );
    const clients = clientDocs
      .filter((doc) => doc.exists())
      .map((doc) => ({ id: doc.id, ...doc.data() }));
    setClientsDetails(clients);
  }
};

// Client delete handlers
const handleDeleteClient = (client) => {
  setClientToDelete(client);
  setIsDeleteDialogOpen(true);
};

const confirmDeleteClient = async () => {
  if (!clientToDelete) return;

  try {
    const clientId = clientToDelete.id;
    const batch = writeBatch(firestore);

    // Find EVERY flat referencing this client (independent of page context / location.state),
    // clean the reference, reset status when it becomes empty, and drop the client's payments.
    // Everything commits in one batch together with the customer delete -> atomic, so a flat can
    // never be left pointing at a deleted client (the bug that orphaned A26/A35).
    const investmentsSnap = await getDocs(collection(firestore, 'investments'));
    for (const invDoc of investmentsSnap.docs) {
      const flatsSnap = await getDocs(collection(firestore, `investments/${invDoc.id}/flats`));
      for (const flatDoc of flatsSnap.docs) {
        const clients = flatDoc.data().clients || [];
        if (!clients.includes(clientId)) continue;

        const updatedClients = clients.filter((id) => id !== clientId);
        const updateData = { clients: updatedClients };
        if (updatedClients.length === 0) {
          updateData.status = 'available';
          updateData.contract_number = null;
          updateData.contract_date = null;
        }
        batch.update(flatDoc.ref, updateData);

        const paymentsSnap = await getDocs(
          collection(firestore, `investments/${invDoc.id}/flats/${flatDoc.id}/payments`)
        );
        paymentsSnap.forEach((paymentDoc) => {
          if (paymentDoc.data().clientId === clientId) batch.delete(paymentDoc.ref);
        });
      }
    }

    // Delete the customer document in the SAME batch.
    batch.delete(doc(firestore, 'customers', clientId));

    await batch.commit();

    showSuccess('Klient został usunięty');

    // If all clients deleted, navigate back to customers page
    if (clientsDetails.length === 1) {
      navigate('/customers');
    } else {
      // Refresh clients list
      setClientsDetails((prev) =>
        prev.filter((c) => c.id !== clientToDelete.id)
      );
    }
  } catch (error) {
    console.error('Error deleting client:', error);
    showError('Nie udało się usunąć klienta');
  } finally {
    setIsDeleteDialogOpen(false);
    setClientToDelete(null);
  }
};

  return (
    <PageWrapper>
      <PageTitle>Klient - Szczegóły</PageTitle>

      <div className="grid grid-cols-[1fr_447px] max-1400:grid-cols-1 gap-[13px] mt-[20px] items-start">
        {loading ? (
          <CardSkeleton />
        ) : (
        <Card className="pt-[20px]">
          <BuildingInfo
            title={investmentDetails?.name || 'Inwestycja'}
            type="Inwestycja"
            imagePath={BuildingImage}
            none_used="tak"
          />

          <UserContacts
            className="mt-[20px]"
            address={investmentDetails?.location || '-'}
            userName={investmentDetails?.manager?.name || investmentDetails?.manager || '-'}
            phoneNumber={investmentDetails?.manager?.phone || investmentDetails?.phone || '-'}
          />

          <div className="mt-[22px]">
            <BlockTitle>Szczegóły Klienta</BlockTitle>

            <DetailsList className="mt-3" items={CUSTOMER_DETAILS} />

            <div className="flex gap-4">
              {clientsDetails.map((client, index) => (
                <div key={client.id} className="relative">
                  <DetailsList
                    className="mt-3"
                    items={[
                      { title: `Nabywca ${index + 1}`, value: client.surname ? `${client.name} ${client.surname}` : (client.name || '-') },
                      { title: 'Adres', value: client.buyers?.[0]?.address || client.address || '-' },
                      { title: 'PESEL', value: client.buyers?.[0]?.pesel || client.pesel || '-' },
                    ]}
                  />
                  <div className="absolute top-3 right-0 flex gap-1">
                    <button
                      onClick={() => handleEditClient(client)}
                      className="p-1 text-[var(--maroon)] hover:opacity-70 transition-opacity"
                      title="Edytuj"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDeleteClient(client)}
                      className="p-1 text-[var(--maroon)] hover:opacity-70 transition-opacity"
                      title="Usuń"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <DetailsList className="mt-3" items={CUSTOMER_OTHER_DETAILS} />

            <div className="mt-4 flex gap-2 flex-wrap">
              <Button variant="bordered" onClick={() => setIsInvoiceModalOpen(true)}>
                Wygeneruj fakture
              </Button>
              <Button
                variant="bordered"
                title="Wygeneruj zgłoszenie ZAUMDE do DFG"
                onClick={() => setIsZaumdeModalOpen(true)}
              >
                Zgłoszenie DFG
              </Button>
            </div>
          </div>

          <div className="mt-[22px]">
           {/* <BlockTitle>Pliki Klienta:</BlockTitle>*/}

           {/*<ClientFilesList className="mt-[21px] max-768:grid max-768:grid-cols-2 max-500:grid-cols-1" files={CLIENT_FILES} /> */}
          </div>
        </Card>
        )}

        <CustomerEmailHistory
          flatId={flatId}
          customerId={clientId?.[0]}
          flatCode={flatDetails?.code}
        />
      </div>

      {loading ? (
        <TableSkeleton className="mt-[28px]" rows={5} columns={8} />
      ) : (
        <EditableInstallmentsTable
          investmentId={investmentId}
          flatId={flatId}
          flatCode={flatDetails?.code || ''}
          className="mt-[28px]"
          topContent={<BlockTitle>Harmonogram płatności</BlockTitle>}
        />
      )}

      {!loading && (
        <SurchargeSection
          investmentId={investmentId}
          flatId={flatId}
        />
      )}

      {!loading && (
        <FinalInvoicesSection
          investmentId={investmentId}
          flatId={flatId}
          flatDetails={flatDetails}
          clientsDetails={clientsDetails}
          investmentDetails={investmentDetails}
        />
      )}

      <EditFlatRemindersModal
        isOpen={isEditInvestmentModalOpen}
        onClose={closeEditInvestmentModal}
        investmentId={investmentId}
      />

      <EditClientModal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        client={selectedClient}
        onSave={handleClientSaved}
      />

      <GenerateInvoiceModal
        isOpen={isInvoiceModalOpen}
        onClose={() => setIsInvoiceModalOpen(false)}
        clientsDetails={clientsDetails}
        flatDetails={flatDetails}
        investmentDetails={investmentDetails}
      />

      <GenerateZaumdeModal
        isOpen={isZaumdeModalOpen}
        onClose={() => {
          setIsZaumdeModalOpen(false);
          setRefreshKey((k) => k + 1);
        }}
        investmentId={investmentId}
        flatId={flatId}
        flatDetails={flatDetails}
        investmentDetails={investmentDetails}
      />

      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        onConfirm={confirmDeleteClient}
        onCancel={() => {
          setIsDeleteDialogOpen(false);
          setClientToDelete(null);
        }}
        title="Usuń klienta"
        message={`Czy na pewno chcesz usunąć klienta ${clientToDelete?.name || ''} ${clientToDelete?.surname || ''}? Ta operacja usunie również wszystkie powiązane płatności i jest nieodwracalna.`}
        confirmText="Usuń"
        cancelText="Anuluj"
        variant="danger"
      />
    </PageWrapper>
  );
};

export default Payments;
